import {defineStore} from 'pinia'
import { ref, computed } from 'vue'
import { OrdersService } from '@/services/orders.service'
import NotificationMessageService from '@/services/notificationmessage.service'
import { Order, SummaryPayload } from '@/entities/order'
import { OrderStatus } from '@/entities/order-status'
import { Notification } from '@/entities/notification'
import { OrderSupportingFile } from '@/entities/order-supporting-file'
import { Patient } from '@/entities/patient'
import { FormTab } from '@/entities/form-tab'
import { FormStep } from '@/entities/form-step'
import { FormControl } from '@/entities/form-control'
import { useDateHelper, normalizeDate, getDateSearchStrings } from '@/composables/useDateHelper'
import { CONSENT_FORM, FORM_CONTROLS, FORM_FLAGS, ORDER_INFO_PAGE_OPTIONS, ORDER_STATUS } from '@/utils/constants'
import { CHECKBOX } from '@/entities/form-control-types'
import { useI18n } from 'vue-i18n'
import { createUploadError, FileWithComment, SyncFilesResult, UploadError, UploadErrorCode } from "@/entities/file";
import {JsonForm} from "@/entities/json-form";
import {convertKeysToCamel} from "@/utils";

const createInitialOrder = (): Order => ({
  orderId: 0,
  cegatId: '',
  orderDate: '',
  selectedTest: '',
  statusId: '',
  referenceId: '',
  labOrderId: '',
  comment: '',
  doctorId: '',
  patientId: null,
  patientDateOfBirth: '',
  patientFirstName: '',
  patientLastName: '',
  ownerFirstName: '',
  ownerLastName: '',
  patientSexId: '',
  formTestId: 0,
  formTestName: '',
  formTestTitle: '',
  form: {} as JsonForm,
  formPanelName: '',
  formCategoryName: '',
  hasReports: false,
}) as Order

export const useOrdersStore = defineStore('orders', () => {
  const {t} = useI18n()
  const service = new OrdersService();

  type SearchField = 'orderId' | 'cegatId';
  const cache = ref<Map<string, Order[]>>(new Map());
  const lastLoadedContext = ref<string>('');

  const searchInput = ref('')
  const searchOrderId = ref('')
  const searchCegatId = ref('')
  const searchOrderIdPartial = ref('')
  const searchCegatIdPartial = ref('')
  const doctorId = ref('')
  const loadingCount = ref(0)
  const gridContentType = ref<'My Orders' | 'Shared Orders'>('My Orders')
  const orders = ref<Order[]>([])
  const order = ref<Order>(createInitialOrder());
  const ordersStatus = ref<OrderStatus[]>([])
  const orderNotifications = ref<Notification[]>([])
  const supportingFiles = ref<OrderSupportingFile[]>([])
  const orderPatient = ref<Patient | null>(null)
  const lastSavedOrder = ref<Order | null>(null);

  const matchesSearch = (value: string | number | null | undefined, search: string): boolean =>
    String(value ?? '').toLowerCase().includes(search)

  function buildSearchIndexes(orderList: Order[]): Order[] {
    return orderList.map(order => ({
      ...order,
      searchIndex: {
        orderDate: order.orderDate ? getDateSearchStrings(order.orderDate) : [],
        patientDateOfBirth: order.patientDateOfBirth ? getDateSearchStrings(order.patientDateOfBirth) : [],
      }
    }))
  }

  const getOrders = computed(() => {
    let filteredOrders = [...orders.value];
    const statusNameMap = new Map<string, string>(
      ordersStatus.value.map(s => [s.id, (t(s.name) ?? '').toString().toLowerCase()])
    );

    if (searchOrderId.value) {
      filteredOrders = filteredOrders.filter(o =>
        o.orderId.toString() === searchOrderId.value
      );
    }
    else if (searchCegatId.value) {
      const search = searchCegatId.value.trim();

      filteredOrders = filteredOrders.filter(o =>
        o.cegatId === search ||
        o.cegatId?.toString()
          .split(/[\s,;]+/)
          .filter(Boolean)
          .includes(search)
      );
    }

    else if (searchInput.value) {
      const search = (searchInput.value || '').toLowerCase();
      filteredOrders = filteredOrders.filter(order =>
        order.orderId.toString().includes(search) ||
        (order.searchIndex?.orderDate ?? []).some(str => str.includes(search)) ||
        matchesSearch(order.referenceId, search) ||
        matchesSearch(order.cegatId, search) ||
        matchesSearch(order.patientFirstName, search) ||
        matchesSearch(order.patientLastName, search) ||
        (order.searchIndex?.patientDateOfBirth ?? []).some(str => str.includes(search)) ||
        matchesSearch(order.ownerFirstName, search) ||
        matchesSearch(order.ownerLastName, search) ||
        matchesSearch(order.formCategoryName, search) ||
        matchesSearch(order.formPanelName, search) ||
        matchesSearch(order.formTestName, search) ||
        matchesSearch((order.formTestName ? t(order.formTestName) : ''), search) ||
        (statusNameMap.get(order.statusId) || '')?.includes(search)
      )
    }

    return filteredOrders.filter(o => o !== null)
  })

  const getOrder = computed(() => order.value)
  const getOrderForm = computed(() => order.value?.form)
  const getOrderFormControls = computed(() => order.value?.form?.controls || [])
  const getOrdersStatus = computed(() => ordersStatus.value)
  const getOrderNotifications = computed(() => orderNotifications.value)
  const getOrderSupportingFiles = computed(() => supportingFiles.value)
  const getSearchInput = computed(() => searchInput.value)
  const getSearchOrderId = computed(() => searchOrderId.value)
  const getSearchCegatId = computed(() => searchCegatId.value)
  const getGridContentType = computed(() => gridContentType.value)
  const getLoadingCount = computed(() => loadingCount.value)
  const getOrderInfoPage = computed(() => {
    const usFormControl = findUsFormControl(order.value.form?.controls);

    if (usFormControl?.value === true) {
      return ORDER_INFO_PAGE_OPTIONS.us_form;
    }

    const patientInfoTab = findPatientInfoTab(order.value.form?.tabs);

    if (!patientInfoTab) {
      return ORDER_INFO_PAGE_OPTIONS.default;
    }

    const patientInfoTabControl = findPatientInfoOrderControl(
      order.value.form?.controls,
      patientInfoTab
    );

    return patientInfoTabControl?.orderInfo.page
      ?? ORDER_INFO_PAGE_OPTIONS.default;
  });

  const findUsFormControl = (
    controls?: FormControl[]
  ): FormControl | undefined =>
    findItem(
      controls,
      (control: FormControl) =>
        control.name === FORM_CONTROLS.USA_Reimbursement &&
        control.type === CHECKBOX
    );

  const getOrderFormTabById = computed(() => (tabId: string) =>
    findItem(order.value.form?.tabs, (tab: FormTab) => tab.uniqueId === tabId)
  );

  const getOrderFormStepBySortOrder = computed(() => (sortOrder: number) =>
    findItem(order.value.form?.steps, (step: FormStep) => step.sortOrder === sortOrder)
  );

  const getOrderFormControlById = computed(() => (controlId: string) =>
    findItem(order.value.form?.controls, (control: FormControl) => control.uniqueId === controlId)
  );

  const getOrderFormControlByName = computed(() => (controlName: string) =>
    findItem(order.value.form?.controls, (control: FormControl) => control.name === controlName)
  );

  const getOrderFormControlsByName = computed(() => (controlName: string) =>
    filterItems(order.value.form?.controls, (control: FormControl) => control.name === controlName)
  );

  const getOrderFormTabsByFlag = computed(() => (flag: string) =>
    filterItems(order.value.form?.tabs, (tab: FormTab) => tab.flag === flag)
  );

  const getOrderFormTabByControlId = computed(() => (controlId: string) =>
    findItem(
      order.value.form?.tabs,
      (tab: FormTab) => tab.controls.includes(controlId)
    )
  );

  const findPatientInfoTab = (tabs?: FormTab[]): FormTab | undefined =>
    findItem(tabs, (tab: FormTab) => tab.flag === FORM_FLAGS.isPatientInfo);

  const findPatientInfoOrderControl = (
    controls: FormControl[] | undefined,
    patientInfoTab: FormTab
  ): FormControl | undefined =>
    findItem(
      controls,
      (control: FormControl) =>
        patientInfoTab.controls.includes(control.uniqueId) &&
        control.type === CHECKBOX &&
        control.orderInfo &&
        control.value === true
    );

  const findItem = <T>(
    items: T[] | undefined,
    predicate: (item: T) => boolean
  ): T | undefined => {
    if (!items) {
      return undefined;
    }

    for (const item of items) {
      if (predicate(item)) {
        return item;
      }
    }

    return undefined;
  };

  const filterItems = <T>(
    items: T[] | undefined,
    predicate: (item: T) => boolean
  ): T[] => {
    const result: T[] = [];

    if (!items) {
      return result;
    }

    for (const item of items) {
      if (predicate(item)) {
        result.push(item);
      }
    }

    return result;
  };

  function getOrdersPatient(): Patient | null {
    if (!order.value.patientId) {
      return null;
    }

    return {
      patientId: order.value.patientId,
      firstName: order.value.patientFirstName,
      lastName: order.value.patientLastName,
      dateOfBirth: order.value.patientDateOfBirth,
      sexId: order.value.patientSexId,
      externalId: order.value.referenceId
    }
  }

  // === ACTIONS ===
  // Loading Status
  function incrementOrdersLoadingCount() { loadingCount.value++ }
  function decrementOrdersLoadingCount() { loadingCount.value-- }

  async function fetchOrders(forceUpdate = false): Promise<void> {
    try {
      incrementOrdersLoadingCount();
      if (searchOrderId.value) {
        await fetchOrdersByOrderId(searchOrderId.value);
      } else if (searchCegatId.value) {
        await fetchOrdersByCegatId(searchCegatId.value);
      } else {
        const result = await service.getOrders(forceUpdate);
        if (result) {
          const searchIndexes = buildSearchIndexes(result);
          orders.value = searchIndexes;
          cache.value.set('all', searchIndexes);
          lastLoadedContext.value = 'all';
        }
      }
    } catch {
      NotificationMessageService.showError("Error while fetching orders");
    } finally {
      decrementOrdersLoadingCount();
    }
  }

  async function fetchOrdersByPhysicianId(physicianId: string): Promise<void> {
    try {
      incrementOrdersLoadingCount();
      const result = await service.getOrdersByPhysicianId(physicianId)
      if (result) {
        const searchIndexes = buildSearchIndexes(result);
        orders.value = searchIndexes;
        cache.value.set(physicianId, searchIndexes);
        lastLoadedContext.value = physicianId;
      }
    } catch (_) {
      NotificationMessageService.showError(`Error while fetching orders`);
    } finally {
      decrementOrdersLoadingCount();
    }
  }

  async function fetchOrdersByOrderId(id: string): Promise<void> {
    try {
      incrementOrdersLoadingCount()
      const result = await service.getOrdersByOrderId(id)
      if (result) orders.value = buildSearchIndexes(result)
    } catch {
      NotificationMessageService.showError('Error while fetching orders')
    } finally {
      decrementOrdersLoadingCount()
    }
  }

  async function fetchOrdersByCegatId(id: string): Promise<void> {
    try {
      incrementOrdersLoadingCount()
      const result = await service.getOrdersByCegatId(id)
      if (result) orders.value = buildSearchIndexes(result)
    } catch {
      NotificationMessageService.showError('Error while fetching orders')
    } finally {
      decrementOrdersLoadingCount()
    }
  }

  async function fetchOrder([id, physicianId]: [number, string?]): Promise<void> {
    try {
      incrementOrdersLoadingCount()
      const result = await service.getOrder(id, physicianId)
      if (result) {
        if (result.form?.controls) {
          result.form.controls.forEach((c) => {
            if (c.type === 'date') {
              c.value = normalizeDate(c.value)
            } else if (c.type === 'dropDown') {
              if (!c.items.includes(c.value))
                c.value = null
            }
          })
        }

        order.value = result
        lastSavedOrder.value = JSON.parse(JSON.stringify(order.value));
        return
      }
    } catch {
      NotificationMessageService.showError(`Error while fetching order: ${id}`)
    } finally {
      decrementOrdersLoadingCount()
    }
    resetOrder()
  }

  function resetOrder(): void {
    order.value = createInitialOrder()
  }

  function resetOrders() {
    orders.value = []
  }

  async function fetchOrdersStatus(forceUpdate = false): Promise<void> {
    try {
      const result = await service.getOrdersStatus(forceUpdate)
      if (result) {
        ordersStatus.value = result.map((item: any) => convertKeysToCamel(item));
      }
    } catch {
      NotificationMessageService.showError('Error while fetching orders status')
    }
  }

  async function fetchOrderNotifications(orderId: number): Promise<void> {
    try {
      const result = await service.getOrderNotifications(orderId)
      if (result) orderNotifications.value = result
    } catch {
      NotificationMessageService.showError('Error fetching order notifications')
    }
  }

  async function postOrder(physicianId?: string): Promise<void> {
    if (!order.value.orderId) {
      const patientData = {
        patientId: order.value.patientId,
        patientFirstName: order.value.patientFirstName,
        patientLastName: order.value.patientLastName,
        patientDateOfBirth: order.value.patientDateOfBirth,
        patientSexId: order.value.patientSexId,
        cegatId: order.value.cegatId,
        referenceId: order.value.referenceId,
      };

      try {
        const result = await service.postOrder(order.value, physicianId)
        if (result) {
          order.value = { ...result, ...patientData }
          lastSavedOrder.value = JSON.parse(JSON.stringify(order.value));
          const duration = order.value.statusId === ORDER_STATUS.draft ? 3000 : undefined;
          NotificationMessageService.showSuccess('New Order Recorded!', null, duration);

          await fetchOrders(!!physicianId)
        }
      } catch {
        NotificationMessageService.showError('Error while creating order')
      }
    }
  }

  async function putOrder(
    [showNotification, physicianId, recipientGroupIds]: [
      boolean,
      string?,
      number[]?
    ]
  ): Promise<boolean | null> {
    if (order.value.orderId === 0) return null

    const hasChanges = lastSavedOrder.value
      ? JSON.stringify(order.value) !== JSON.stringify(lastSavedOrder.value)
      : true;

    try {
      const result = await service.putOrder(order.value, physicianId, recipientGroupIds)
      if (result) {
        const prevStatus = lastSavedOrder.value?.statusId;

        lastSavedOrder.value = JSON.parse(JSON.stringify(order.value));

        if (showNotification && hasChanges) {
          if (prevStatus === ORDER_STATUS.draft && order.value.statusId !== ORDER_STATUS.draft && !!order.value.statusId) {
            NotificationMessageService.showSuccess('Order Submitted!');
          } else if (order.value.statusId !== ORDER_STATUS.draft) {
            NotificationMessageService.showSuccess('Order Information Updated!');
          }
        }

        if (physicianId) {
          await fetchOrdersByPhysicianId(physicianId)
        } else {
          await fetchOrders(true)
        }

        return false
      }
    } catch {
      if (showNotification) {
        NotificationMessageService.showError(
          'Error while updating order: {order}',
          { order: order.value.orderId }
        )
      }
    }

    return true
  }

  async function deleteOrder([id, physicianId]: [number, string?]): Promise<void> {
    if (!id) return
    try {
      const err = await service.deleteOrder(id, physicianId)
      if (!err) {
        NotificationMessageService.showSuccess('Order Deleted!')

        if (physicianId) {
          await fetchOrdersByPhysicianId(physicianId)
        } else {
          await fetchOrders(true)
        }
      }
    } catch {
      NotificationMessageService.showError('Error deleting order')
    }
  }

  async function postOrderNotification(postData: any): Promise<boolean> {
    if (postData && postData.orderId > 0 && postData.notification) {
      try {
        const result = await service.postOrderNotification(postData.orderId, postData.notification);
        if (!result) return true;
      } catch (_) {
        NotificationMessageService.showError(
          'Error while posting new order notification - order id: {orderId}',
          { orderId: postData.orderId }
        );
      }
    }
    return false;
  }

  async function deleteOrderNotification(postData: any): Promise<boolean> {
    if (postData && postData.orderId > 0 && postData.notificationId) {
      try {
        const result = await service.deleteOrderNotification(postData.orderId, postData.notificationId);
        if (!result) return true;
      } catch (_) {
        NotificationMessageService.showError(
          'Error while deleting notification - id: {notificationId}, of order id: {orderId}',
          { notificationId: postData.notificationId, orderId: postData.orderId }
        );
      }
    }
    return false;
  }

  async function fetchOrderSupportingFiles(orderId: number, showNotifications: boolean = true): Promise<void> {
    if (orderId > 0) {
      try {
        const files = await service.getOrderSupportingFiles(orderId);
        if (files != null) {
          supportingFiles.value = files;
        }
      } catch (_) {
        if (showNotifications)
          NotificationMessageService.showError('Error while fetching order supporting files - order id: {orderId}', { orderId });
        else
          console.log('Error while fetching order supporting files - order id: {orderId}', { orderId })
      }
    } else {
      supportingFiles.value = [];
    }
  }

  async function postOrderSupportingFile(data: any, showNotifications: boolean = true): Promise<boolean> {
    if (data && order.value.orderId > 0 && data.file) {
      try {
        const result = await service.postOrderSupportingFile(order.value.orderId, data.file, data.comment);
        if (result) {
          if (showNotifications)
            NotificationMessageService.showSuccess('New Supporting File Recorded! ({name})', { name: data.file.name });
          await fetchOrderSupportingFiles(order.value.orderId);
          return true;
        }
      } catch (_) {
        if (showNotifications)
          NotificationMessageService.showError('Error while posting new file - order id: {orderId}', { orderId: order.value.orderId });
      }
    } else {
      if (showNotifications)
        NotificationMessageService.showError('Error while posting new file - expected input not provided');
    }
    return false;
  }

  async function postOrderSupportingFilesBulk(orderId: number, formData: FormData): Promise<{ success: boolean; uploadedFiles?: OrderSupportingFile[]; error?: string }> {
    try {
      let files = await service.postOrderSupportingFilesBulk(orderId, formData);

      if (files && typeof files === 'object' && !Array.isArray(files) && files.files) {
        files = files.files;
      }

      if (!Array.isArray(files)) {
        files = [];
      }
      return { success: true, uploadedFiles: files };
    } catch (err: any) {
      const errorMsg = err?.message || 'Unknown error';
      return { success: false, error: errorMsg };
    }
  }

  async function syncOrderSupportingFiles(orderId: number, files: FileWithComment[]): Promise<SyncFilesResult> {
    const errors: UploadError[] = [];
    let addedCount = 0;

    const existingFiles = supportingFiles.value || [];
    const replaceFiles: { existing: OrderSupportingFile; file: FileWithComment }[] = [];
    const newFiles: FileWithComment[] = [];

    files.forEach(file => {
      const existing = existingFiles.find(f => f.fileName === file.file.name);
      if (existing) {
        replaceFiles.push({ existing, file });
      } else {
        newFiles.push(file);
      }
    });

    for (const rf of replaceFiles) {
      try {
        await putOrderSupportingFile({
          orderFileId: rf.existing.orderFileId,
          comment: rf.file.comment,
          file: rf.file.file || null,
        }, false);
      } catch (e) {
        errors.push(createUploadError(rf.file.file.name, UploadErrorCode.REPLACE_FAILED, e.message));
      }
    }

    if (newFiles.length > 0) {
      const { added, errs } = await uploadNewFiles(orderId, newFiles);
      addedCount += added;
      errors.push(...errs);
    }

    await fetchOrderSupportingFiles(orderId, false);
    return { success: errors.length === 0, addedCount, errors };
  }

  async function uploadNewFiles(orderId: number, newFiles: FileWithComment[]): Promise<{ added: number; errs: UploadError[] }> {
    const errs: UploadError[] = [];
    let added = 0;

    const formData = new FormData();
    newFiles.forEach((nf, i) => {
      formData.append(`files[${i}][file]`, nf.file);
      formData.append(`files[${i}][comment]`, nf.comment ?? "");
    });

    const bulkResult = await postOrderSupportingFilesBulk(orderId, formData);
    if (bulkResult.success) {
      added = bulkResult.uploadedFiles?.length || 0;
      await fetchOrderSupportingFiles(orderId, false);
    } else {
      for (const file of newFiles) {
        try {
          const singleResult = await postOrderSupportingFile({
            file: file.file,
            comment: file.comment
          }, false);
          if (singleResult) {
            added++;
          } else {
            errs.push(createUploadError(file.file.name, UploadErrorCode.UPLOAD_FAILED, 'Unknown error'));
          }
        } catch (e) {
          errs.push(createUploadError(file.file.name, UploadErrorCode.UPLOAD_FAILED, e.message));
        }
      }

      if (added > 0) {
        await fetchOrderSupportingFiles(orderId, false);
      }

      if (added === 0 && newFiles.length > 0) {
        errs.push(createUploadError('multiple', UploadErrorCode.BULK_FAILED, bulkResult.error));
      }
    }

    return { added, errs };
  }

  async function putOrderSupportingFile(data: any, showNotifications: boolean = true): Promise<boolean> {
    if (data && order.value.orderId > 0 && data.orderFileId > 0) {
      try {
        const result = await service.putOrderSupportingFile(order.value.orderId, data.orderFileId, data.file || undefined, data.comment);
        if (result) {
          if (showNotifications)
            NotificationMessageService.showSuccess('Supporting File Updated!');
          else
            console.log('Supporting File Updated!')
          await fetchOrderSupportingFiles(order.value.orderId);
          return true
        }
      } catch (_) {
        if (showNotifications)
          NotificationMessageService.showError('Error while updating file - order id: {orderId}', { orderId: order.value.orderId });
        else
          console.log('Error while updating file - order id: {orderId}', { orderId: order.value.orderId })
      }
    } else {
      if (showNotifications)
        NotificationMessageService.showError('Error while updating file - expected input not provided');
      else
        console.log('Error while updating file - expected input not provided')
    }
    return false
  }

  async function deleteOrderSupportingFile(data: any, showNotifications: boolean = true ): Promise<boolean> {
    if (data && order.value.orderId > 0 && data.orderFileId > 0) {
      try {
        await service.deleteOrderSupportingFile(order.value.orderId, data.orderFileId);
        if (showNotifications)
          NotificationMessageService.showSuccess('Supporting File Deleted!');
        else
          console.log('Supporting File Deleted!')
        await fetchOrderSupportingFiles(order.value.orderId);
        return true
      } catch (_) {
        if (showNotifications)
          NotificationMessageService.showError(
          'Error while deleting file - id: {orderFileId}, of order id: {orderId}',
            { orderFileId: data.orderFileId, orderId: order.value.orderId }
          )
        else
          console.log('Error while deleting file - id: {orderFileId}, of order id: {orderId}', { orderFileId: data.orderFileId, orderId: order.value.orderId })
      }
    } else {
      if (showNotifications)
        NotificationMessageService.showError('Error while deleting file - expected input not provided');
      else
        console.log('Error while deleting file - expected input not provided')
    }

    return false
  }

  async function fetchOrderConsent(data: any): Promise<void> {
    if (data && data.orderId > 0 && data.type && data.data) {
      try {
        await service.getOrderConsent(data.orderId, data.type, data.data, data.template || CONSENT_FORM.defaultTemplate);
      } catch (_) {
        NotificationMessageService.showError('Error while loading order consent form');
      }
    } else {
      NotificationMessageService.showError('Error while loading order consent form - expected input not provided');
    }
  }

  async function fetchOrderSummary(data: SummaryPayload): Promise<void> {
    if (data && data.orderId > 0) {
      try {
        await service.getOrderSummary(data.orderId, data.doctorId ?? '', data.languageId ?? 'en');
      } catch (_) {
        NotificationMessageService.showError('Error while loading order summary');
      }
    } else {
      NotificationMessageService.showError('Error while loading order summary - expected input not provided');
    }
  }

  async function fetchOrderSummaryBlob(data: SummaryPayload): Promise<Blob> {
    if (data && data.orderId > 0) {
      return await service.getOrderSummaryBlob(data.orderId, data.doctorId ?? '', data.languageId ?? 'en');
    } else {
      throw new Error('Error while loading order summary - expected input not provided');
    }
  }

  function setOrders(value: Order[]): void { orders.value = value }
  function setSearchInput(value: string): void { searchInput.value = value }
  function setGridContentType(value: 'My Orders' | 'Shared Orders'): void { gridContentType.value = value }
  function setSearchOrderId(value: string): void { searchOrderId.value = value }
  function setSearchCegatId(value: string): void { searchCegatId.value = value }

  function setOrderPatient(patient: Patient): void {
    if (!patient) return;

    orderPatient.value = patient;

    order.value.patientId = patient.patientId;
    order.value.patientFirstName = patient.firstName;
    order.value.patientLastName = patient.lastName;
    order.value.patientDateOfBirth = patient.dateOfBirth;
    order.value.patientSexId = patient.sexId;
    order.value.cegatId = patient.patientNumber ?? '';
    order.value.referenceId = patient.externalId ?? '';
  }

  function resetOrderSupportingFiles() {
    supportingFiles.value = []
  }

  function clearOrderPatient(): void {
    orderPatient.value = null;

    order.value.patientId = null;
    order.value.patientFirstName = '';
    order.value.patientLastName = '';
    order.value.patientDateOfBirth = '';
    order.value.patientSexId = '';
    order.value.cegatId = '';
    order.value.referenceId = '';
  }

  function restoreOrdersFromCache(context: string): boolean {
    const cached = cache.value.get(context);

    if (!cached) {
      return false;
    }

    orders.value = cached;
    lastLoadedContext.value = context;

    return true;
  }

  function findCachedOrders(
    field: SearchField,
    value: string
  ): Order[] {
    const allOrders = cache.value.get('all') ?? [];

    return allOrders.filter(order =>
      field === 'orderId'
        ? order.orderId.toString() === value
        : order.cegatId === value
    );
  }

  function removeOrderFromCurrentList(orderId: number): void {
    orders.value = orders.value.filter(o => o.orderId !== orderId);

    const context = lastLoadedContext.value;
    if (context && cache.value.has(context)) {
      const cached = cache.value.get(context)!;
      cache.value.set(context, cached.filter(o => o.orderId !== orderId));
    }

    if (cache.value.has('all')) {
      const allCached = cache.value.get('all')!;
      cache.value.set('all', allCached.filter(o => o.orderId !== orderId));
    }
  }

  // === RETURN ===
  return {
    searchInput, searchOrderId, searchCegatId, loadingCount, gridContentType,
    orders, order, ordersStatus, orderNotifications, supportingFiles, orderPatient,

    // Getters
    getOrders, getOrder, getOrderForm, getOrderFormControls,
    getOrderFormTabById, getOrderFormStepBySortOrder,
    getOrderFormControlById, getOrderFormControlByName,
    getOrderFormTabByControlId, getOrdersStatus,
    getOrderNotifications, getOrderSupportingFiles,
    getOrdersPatient, getSearchInput, getSearchOrderId, getSearchCegatId,
    getGridContentType, getLoadingCount, getOrderInfoPage, getOrderFormControlsByName, getOrderFormTabsByFlag,

    // Actions
    fetchOrders, fetchOrdersByOrderId, fetchOrdersByCegatId, fetchOrdersByPhysicianId, findCachedOrders,
    fetchOrder, resetOrder, resetOrders, fetchOrdersStatus, fetchOrderNotifications,
    postOrder, putOrder, deleteOrder, setSearchInput, setGridContentType, setSearchOrderId,
    setSearchCegatId, setOrderPatient, resetOrderSupportingFiles, fetchOrderSupportingFiles,
    deleteOrderNotification, deleteOrderSupportingFile, postOrderNotification, restoreOrdersFromCache,
    postOrderSupportingFile, putOrderSupportingFile, fetchOrderConsent, fetchOrderSummary,
    postOrderSupportingFilesBulk, syncOrderSupportingFiles, clearOrderPatient, setOrders,
    fetchOrderSummaryBlob, removeOrderFromCurrentList
  }
})
